import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Activity, 
  Check,
  CreditCard,
  ArrowLeft,
  Shield,
  Loader2
} from 'lucide-react';
import { FigIcon } from '../components/FigIcon';
import { toast } from 'sonner';

const CheckoutPage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const packageId = searchParams.get('package') || 'one_time';
  const sessionId = searchParams.get('session_id');

  const packages = {
    results: { name: 'Results Unlock', price: '£5', description: 'See your score and risk factors' },
    one_time: { name: 'Full Program', price: '£49', description: 'Results + 90-day correction plan' },
    subscription: { name: '3-Month Plan', price: '£19/mo', description: 'Monthly payments for 3 months' }
  };

  const packageFeatures = {
    results: ['Metabolic Trajectory Score', 'Risk tier assessment', 'Top 5 risk drivers', 'Component breakdown'],
    one_time: ['Everything in Results', 'Personalized 90-day plan', '4 targeted rules', 'Weekly check-ins', 'Progress dashboard'],
    subscription: ['Everything in Full Program', 'Lower upfront cost', 'Cancel anytime', 'Priority support']
  };

  const selectedPackage = packages[packageId] || packages.one_time;

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please check your email for confirmation.');
      setPolling(false);
      return;
    }

    setPolling(true);
    try {
      const response = await api.get(`/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        await refreshUser();
        toast.success('Payment successful! Welcome to Trajectory.');
        navigate('/plan');
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setPolling(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const handleCheckout = async () => {
    if (!user) {
      navigate(`/register?redirect=checkout&package=${packageId}`);
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/payments/create-session', {
        package_id: packageId,
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
    }
  };

  if (polling) {
    return (
      <div className="min-h-screen bg-[#E8E4DC] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#2F3E30] animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Libre Baskerville, serif' }}>VERIFYING PAYMENT</h2>
          <p className="text-[#525252]">Please wait while we confirm your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8E4DC] flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <button 
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-[#525252] hover:text-[#1A1C19] mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="flex items-center gap-2 mb-8">
          <FigIcon className="w-8 h-8 text-[#2F3E30]" />
          <span className="font-bold text-2xl" style={{ fontFamily: 'Libre Baskerville, serif' }}>FIG HEALTH</span>
        </div>

        <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Libre Baskerville, serif' }}>COMPLETE YOUR PURCHASE</h1>
        <p className="text-[#525252] mb-8">Unlock your personalized 90-day correction plan</p>

        {/* Selected Package */}
        <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>{selectedPackage.name}</h3>
              <p className="text-sm text-[#525252]">{selectedPackage.description}</p>
            </div>
            <span className="text-2xl font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>{selectedPackage.price}</span>
          </div>
          <ul className="space-y-2 text-sm">
            {(packageFeatures[packageId] || packageFeatures.one_time).map((item, i) => (
              <li key={i} className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#10B981]" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Switch Package Link */}
        <div className="text-center mb-6">
          {packageId === 'results' ? (
            <button 
              onClick={() => navigate('/pricing')}
              className="text-sm text-[#2F3E30] hover:underline"
            >
              Want the full 90-day plan? View all options
            </button>
          ) : (
            <button 
              onClick={() => navigate(`/checkout?package=${packageId === 'one_time' ? 'subscription' : 'one_time'}`)}
              className="text-sm text-[#2F3E30] hover:underline"
            >
              Switch to {packageId === 'one_time' ? '3-month subscription (£19/mo)' : 'one-time purchase (£49)'}
            </button>
          )}
        </div>

        {/* Checkout Button */}
        <Button
          onClick={handleCheckout}
          disabled={loading}
          className="w-full h-14 bg-[#2F3E30] hover:bg-[#3A4D3B] text-white text-lg font-medium glow-primary"
          data-testid="checkout-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Redirecting to checkout...
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5 mr-2" />
              Pay with Card
            </>
          )}
        </Button>

        {/* Security Note */}
        <div className="flex items-center justify-center gap-2 mt-6 text-sm text-[#525252]">
          <Shield className="w-4 h-4" />
          Secure payment powered by Stripe
        </div>

        {/* Money Back */}
        <p className="text-center text-xs text-[#525252] mt-4">
          30-day money back guarantee if you're not satisfied
        </p>
      </div>
    </div>
  );
};

export default CheckoutPage;
